<template>
  <!-- With a placeholder, the box is a grid: the blurred thumbnail sits on its only cell and the picture on top of
       it. The real image is never hidden: the server-rendered document shows it as is, a fade only ever lifts it.
       The thumbnail switches file on the same media as the picture, so it is always of the picture shown. -->
  <div
    v-if="placeholder"
    v-bind="attributes.picture"
    :class="['grid grid-cols-1 grid-rows-1 overflow-hidden', attributes.picture.class]"
  >
    <picture
      v-if="placeholderShown"
      class="col-start-1 row-start-1"
    >
      <source
        v-if="narrowPlaceholder"
        :media="fileSwitchMedia(properties.breakpoint)"
        :srcset="placeholder"
      />
      <img
        :src="narrowPlaceholder ?? placeholder"
        alt=""
        aria-hidden="true"
        :class="[properties.imgAttrs?.class, 'scale-110 blur']"
      />
    </picture>
    <picture class="col-start-1 row-start-1">
      <template
        v-for="(source, index) of sources"
        :key="source.src"
      >
        <source
          v-if="index + 1 < sources.length"
          :media="source.media"
          :type="source.type"
          :sizes="source.sizes"
          :srcset="source.srcset"
        />
        <img
          v-else
          ref="imgElement"
          :alt="properties.alt"
          v-bind="attributes.img"
          :class="{ 'transition-opacity duration-700 ease-in-out': fading, 'opacity-0': fading && !loaded }"
          :src="source.src"
          :sizes="source.sizes"
          :srcset="source.srcset"
          @transitionend="placeholderShown = false"
        />
      </template>
    </picture>
  </div>
  <picture
    v-else
    v-bind="attributes.picture"
  >
    <template
      v-for="(source, index) of sources"
      :key="source.src"
    >
      <source
        v-if="index + 1 < sources.length"
        :media="source.media"
        :type="source.type"
        :sizes="source.sizes"
        :srcset="source.srcset"
      />
      <img
        v-else
        ref="imgElement"
        :alt="properties.alt"
        v-bind="attributes.img"
        :src="source.src"
        :sizes="source.sizes"
        :srcset="source.srcset"
      />
    </template>
  </picture>
</template>
<script setup lang="ts" generic="Provider extends keyof ConfiguredImageProviders = ProviderDefaults['provider']">
// Fork of NuxtPicture (@nuxt/image v2.0.0), which can only vary the format across its <source> elements.
// Diff against the fork commit to see what we added; re-apply it when @nuxt/image is upgraded.
import type { ConfiguredImageProviders, ProviderDefaults } from '@nuxt/image';
import type { SerializableHead } from '@unhead/vue/types';
import type { PictureProperties } from './variants';
import { fileSwitchMedia, pictureVariants } from './variants';
import { markFeatureUsage, useImageProperties } from './nuxt-image-internals';

defineOptions({ inheritAttrs: false });
defineSlots();

const properties = defineProps<PictureProperties<Provider>>();

const emit = defineEmits<{
  (event: 'load', payload: Event): unknown;
  (event: 'error', payload: string | Event): unknown;
}>();

const _attributes = useAttrs();
const imageAttributeNames = new Set([
  'alt',
  'referrerpolicy',
  'usemap',
  'longdesc',
  'ismap',
  'loading',
  'crossorigin',
  'decoding',
  'nonce',
]);
const attributes = computed(() => {
  const result: Record<'img' | 'picture', Record<string, unknown>> = {
    img: {
      ...normalizedAttributes.value,
      ...properties.imgAttrs,
      'data-nuxt-pic': '',
    },
    picture: {},
  };
  // Only pass down supported <image> attributes
  for (const key in _attributes) {
    if (imageAttributeNames.has(key)) {
      if (!(key in result.img)) {
        result.img[key] = _attributes[key];
      }
    } else {
      result.picture[key] = _attributes[key];
    }
  }
  return result;
});

const originalFormat = computed(() => /^[^?#]+\.(\w+)(?:$|[?#])/.exec(properties.src)?.[1]);

const legacyFormat = computed(() => {
  if (properties.legacyFormat) {
    return properties.legacyFormat;
  }

  const isNotTransparent = !originalFormat.value || !['png', 'webp', 'gif', 'svg'].includes(originalFormat.value);
  return isNotTransparent ? 'jpeg' : 'png';
});

const $img = useImage();
const { providerOptions, imageModifiers, normalizedAttributes } = useImageProperties(properties);

interface Source {
  src?: string;
  srcset?: string;
  type?: string;
  sizes?: string;
  media?: string;
}

// Widest first: the browser takes the first <source> whose media matches, and the last entry is the <img>,
// which carries no media and therefore has to be the default source.
const variants = computed(() => pictureVariants(properties));
const sources = computed<Source[]>(() => {
  let formats: string[];
  if (properties.format) {
    formats = properties.format.split(',');
  } else if (originalFormat.value === 'svg') {
    formats = ['svg'];
  } else {
    formats = $img.options.format?.length ? [...$img.options.format] : ['webp'];
  }

  if (formats[0] === 'svg') {
    return variants.value.map((variant) => ({ src: variant.src, media: variant.media }));
  }

  if (formats.includes(legacyFormat.value)) {
    formats.splice(formats.indexOf(legacyFormat.value), 1);
    formats.push(legacyFormat.value);
  } else {
    formats.push(legacyFormat.value);
  }

  return variants.value.flatMap((variant) =>
    formats.map((format) => {
      const { srcset, sizes, src } = $img.getSizes(variant.src, {
        ...providerOptions.value,
        sizes: variant.ladder || $img.options.screens,
        densities: properties.densities,
        modifiers: { ...imageModifiers.value, format },
      });

      // The ladder only seeded the candidates: what the browser must read is the tier's single size.
      return { src, type: `image/${format}`, sizes: variant.size ?? sizes, srcset, media: variant.media };
    }),
  );
});

// A preload link only means something in the server-rendered document.
if (properties.preload) {
  useHead(
    {
      link: () => {
        const firstSource = sources.value[0];
        if (!firstSource) {
          return [];
        }

        const link: NonNullable<SerializableHead['link']>[number] = {
          rel: 'preload',
          as: 'image',
          imagesrcset: firstSource.srcset,
          nonce: properties.nonce,
          ...(typeof properties.preload !== 'boolean' && properties.preload?.fetchPriority
            ? { fetchpriority: properties.preload.fetchPriority }
            : {}),
        };

        if (firstSource.sizes) {
          link.imagesizes = firstSource.sizes;
        }
        return [link];
      },
    },
    { mode: 'server' },
  );
}

// Decided once: swapping the box for a bare picture later would remount the image and reload it.
const own = properties.noPlaceholder ? undefined : (properties.placeholder ?? imagePlaceholder(properties.src));
const narrowPlaceholder =
  own && properties.narrowSrc ? (properties.narrowPlaceholder ?? imagePlaceholder(properties.narrowSrc)) : undefined;
// A range without a thumbnail of its own picture shows none at all: the other range's would be another picture.
const placeholder = properties.narrowSrc && !narrowPlaceholder ? undefined : own;
const loaded = ref(false);
// The fade only exists in the browser, for an image still on its way: the server-rendered image is never dimmed.
const fading = ref(false);
const placeholderShown = ref(!!placeholder);

const nuxtApp = useNuxtApp();
const initialLoad = nuxtApp.isHydrating;

const imgElement = useTemplateRef<HTMLImageElement | HTMLImageElement[]>('imgElement');
onMounted(() => {
  // the ref sits inside a v-for, so Vue collects it as an array
  const element = Array.isArray(imgElement.value) ? imgElement.value[0] : imgElement.value;
  if (!element) {
    return;
  }

  // A cached image is complete before this listener exists: nothing to fade, the thumbnail has nothing to cover.
  loaded.value = element.complete && element.naturalWidth > 0;
  if (loaded.value) {
    placeholderShown.value = false;
  } else {
    fading.value = !!placeholder;
  }

  // An image that settled before hydration fired its events on nobody. A broken one is complete with
  // no natural size — no inline onerror marker, which the strict CSP (script-src-attr 'none') blocks.
  if (element.complete && initialLoad) {
    if (element.naturalWidth === 0) {
      emit('error', new Event('error'));
    } else {
      emit('load', new Event('load'));
    }
  }

  element.addEventListener('load', (event) => {
    loaded.value = true;
    emit('load', event);
  });

  element.addEventListener('error', (event) => {
    // The fallback image takes the box: a thumbnail of the broken one would lie under it.
    placeholderShown.value = false;
    emit('error', event);
  });

  markFeatureUsage('nuxt-picture');
});
</script>
